// =============================================================================
// stores/theme.store.ts
// R2 Display Experience Engine (DEE) — centralized theme management
// 6 built-in themes + custom builder + auto day/night + per-project overrides
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ─── Theme Names ──────────────────────────────────────────────────────────────
export type R2ThemeId =
  | 'engineering-dark'   // Default — deep midnight blue, teal accent
  | 'apple-white'        // Clean white, glass panels, blue accent
  | 'theater-mode'       // Matte black, laser outlines, max focus
  | 'blueprint'          // Blue engineering background, CAD linework
  | 'founder-gold'       // White + gold accents, premium executive
  | 'starry-night'       // Animated dark with dynamic lighting
  | 'custom';            // User-defined variables

// ─── Theme Definition ─────────────────────────────────────────────────────────
export interface R2ThemeVars {
  // Backgrounds
  bgDeep:   string;   // rgb triple, e.g. "10 22 46"
  bgPanel:  string;
  bgPanel2: string;
  // Borders
  border:   string;
  borderSub: string;
  // Text
  textPrimary:   string;
  textSecondary: string;
  textMuted:     string;
  // Accent
  accent:    string;   // rgb triple
  accent2:   string;
  // Effects
  glowColor: string;   // rgba full string
  shadow:    string;   // full CSS shadow string
  // Surfaces
  glass:     string;   // rgba full string for glass panels
  glassBorder: string; // rgba full string
  // Radius
  radius:    string;   // e.g. "12px"
}

export interface R2ThemeMeta {
  id:          R2ThemeId;
  label:       string;
  description: string;
  dark:        boolean;
  preview:     string;   // CSS gradient for preview swatch
  vars:        R2ThemeVars;
}

// ─── Theme Definitions ─────────────────────────────────────────────────────────
export const R2_THEMES: Record<Exclude<R2ThemeId, 'custom'>, R2ThemeMeta> = {
  'engineering-dark': {
    id: 'engineering-dark', label: 'Engineering Dark', dark: true,
    description: 'Deep midnight blue. Teal accent. Mission-control precision.',
    preview: 'linear-gradient(135deg, #0a162e 0%, #0f203e 50%, #14b8a6 100%)',
    vars: {
      bgDeep: '10 22 46', bgPanel: '15 32 62', bgPanel2: '22 44 80',
      border: '40 68 116', borderSub: '25 45 80',
      textPrimary: '226 236 250', textSecondary: '148 172 210', textMuted: '80 110 155',
      accent: '20 184 166', accent2: '59 130 246',
      glowColor: 'rgba(20,184,166,0.18)',
      shadow: '0 4px 24px rgba(0,0,0,0.45)',
      glass: 'rgba(15,32,62,0.82)', glassBorder: 'rgba(40,68,116,0.55)',
      radius: '12px',
    },
  },
  'apple-white': {
    id: 'apple-white', label: 'Apple White', dark: false,
    description: 'Clean. Quiet. Elegant. Pure white with soft glass panels.',
    preview: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 50%, #3b82f6 100%)',
    vars: {
      bgDeep: '248 249 252', bgPanel: '255 255 255', bgPanel2: '243 244 246',
      border: '226 232 240', borderSub: '241 245 249',
      textPrimary: '15 23 42', textSecondary: '71 85 105', textMuted: '148 163 184',
      accent: '59 130 246', accent2: '99 102 241',
      glowColor: 'rgba(59,130,246,0.12)',
      shadow: '0 2px 16px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05)',
      glass: 'rgba(255,255,255,0.80)', glassBorder: 'rgba(226,232,240,0.9)',
      radius: '10px',
    },
  },
  'theater-mode': {
    id: 'theater-mode', label: 'Theater Mode', dark: true,
    description: 'Matte black. Laser-line panels. Illuminated text. Maximum focus.',
    preview: 'linear-gradient(135deg, #050505 0%, #0d0d0d 50%, #25b5a8 100%)',
    vars: {
      bgDeep: '4 4 4', bgPanel: '10 10 10', bgPanel2: '16 16 16',
      border: '40 40 40', borderSub: '25 25 25',
      textPrimary: '245 245 245', textSecondary: '180 180 180', textMuted: '100 100 100',
      accent: '37 181 168', accent2: '94 234 212',
      glowColor: 'rgba(37,181,168,0.22)',
      shadow: '0 4px 32px rgba(0,0,0,0.7)',
      glass: 'rgba(10,10,10,0.92)', glassBorder: 'rgba(37,181,168,0.18)',
      radius: '8px',
    },
  },
  'blueprint': {
    id: 'blueprint', label: 'Blueprint', dark: true,
    description: 'Blue engineering background. CAD linework. Technical aesthetic.',
    preview: 'linear-gradient(135deg, #0a1e3c 0%, #0d2848 50%, #4fa3e0 100%)',
    vars: {
      bgDeep: '8 24 50', bgPanel: '12 32 64', bgPanel2: '16 42 80',
      border: '60 120 200', borderSub: '35 75 140',
      textPrimary: '210 230 255', textSecondary: '130 170 220', textMuted: '70 110 170',
      accent: '96 165 250', accent2: '148 200 255',
      glowColor: 'rgba(96,165,250,0.2)',
      shadow: '0 4px 24px rgba(0,0,20,0.5)',
      glass: 'rgba(12,32,64,0.85)', glassBorder: 'rgba(60,120,200,0.45)',
      radius: '4px',
    },
  },
  'founder-gold': {
    id: 'founder-gold', label: 'Founder Gold', dark: false,
    description: 'White with gold accents. Reserved for Founder Edition.',
    preview: 'linear-gradient(135deg, #fffbf0 0%, #fef3c7 50%, #d4af37 100%)',
    vars: {
      bgDeep: '254 252 245', bgPanel: '255 255 255', bgPanel2: '254 249 235',
      border: '220 190 120', borderSub: '240 220 170',
      textPrimary: '20 15 5', textSecondary: '100 80 40', textMuted: '160 130 80',
      accent: '212 175 55', accent2: '180 140 30',
      glowColor: 'rgba(212,175,55,0.18)',
      shadow: '0 2px 20px rgba(180,140,30,0.12)',
      glass: 'rgba(255,252,240,0.88)', glassBorder: 'rgba(212,175,55,0.4)',
      radius: '12px',
    },
  },
  'starry-night': {
    id: 'starry-night', label: 'Starry Night', dark: true,
    description: 'Animated cosmic background. Engineering overlays stay crisp.',
    preview: 'radial-gradient(ellipse at top, #0d1b2a 0%, #1a1035 60%, #251545 100%)',
    vars: {
      bgDeep: '8 14 28', bgPanel: '13 20 40', bgPanel2: '18 28 52',
      border: '45 60 110', borderSub: '28 38 75',
      textPrimary: '230 235 255', textSecondary: '150 165 210', textMuted: '80 95 145',
      accent: '140 100 240', accent2: '180 140 255',
      glowColor: 'rgba(140,100,240,0.2)',
      shadow: '0 4px 28px rgba(0,0,0,0.5)',
      glass: 'rgba(13,20,40,0.85)', glassBorder: 'rgba(140,100,240,0.22)',
      radius: '12px',
    },
  },
};

// ─── Auto-Switch Config ────────────────────────────────────────────────────────
export interface AutoSwitchConfig {
  enabled:     boolean;
  dayTheme:    R2ThemeId;   // 06:00–18:00
  nightTheme:  R2ThemeId;   // 18:00–06:00
  presentationTheme: R2ThemeId;
}

// ─── Store Interface ──────────────────────────────────────────────────────────
interface ThemeState {
  activeTheme:      R2ThemeId;
  customVars:       Partial<R2ThemeVars>;
  autoSwitch:       AutoSwitchConfig;
  projectThemes:    Record<string, R2ThemeId>;   // projectId → theme
  accentOverride?:  string;  // custom accent rgb triple
  cornerRadius:     number;  // 0–20px
  transparency:     number;  // 0–1 glass transparency
  shadowIntensity:  number;  // 0–1
  glowIntensity:    number;  // 0–1
  animationLevel:   'off' | 'low' | 'medium' | 'high';

  // Actions
  setTheme:         (id: R2ThemeId) => void;
  setCustomVar:     (key: keyof R2ThemeVars, value: string) => void;
  setAutoSwitch:    (cfg: Partial<AutoSwitchConfig>) => void;
  setProjectTheme:  (projectId: string, theme: R2ThemeId) => void;
  clearProjectTheme: (projectId: string) => void;
  setAccent:        (rgb: string) => void;
  setCornerRadius:  (px: number) => void;
  setTransparency:  (v: number) => void;
  setShadow:        (v: number) => void;
  setGlow:          (v: number) => void;
  setAnimation:     (v: ThemeState['animationLevel']) => void;
  getActiveVars:    () => R2ThemeVars;
  autoDetectTheme:  () => R2ThemeId;
}

const DEFAULT_CUSTOM_VARS: Partial<R2ThemeVars> = {};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      activeTheme:     'engineering-dark',
      customVars:      DEFAULT_CUSTOM_VARS,
      autoSwitch:      {
        enabled:           false,
        dayTheme:          'apple-white',
        nightTheme:        'engineering-dark',
        presentationTheme: 'founder-gold',
      },
      projectThemes:    {},
      accentOverride:   undefined,
      cornerRadius:     12,
      transparency:     0.82,
      shadowIntensity:  0.6,
      glowIntensity:    0.5,
      animationLevel:   'medium',

      setTheme:    (id) => set({ activeTheme: id }),
      setCustomVar: (key, value) =>
        set((s) => ({ customVars: { ...s.customVars, [key]: value } })),
      setAutoSwitch: (cfg) =>
        set((s) => ({ autoSwitch: { ...s.autoSwitch, ...cfg } })),
      setProjectTheme:  (pid, t)   => set((s) => ({ projectThemes: { ...s.projectThemes, [pid]: t } })),
      clearProjectTheme: (pid)     => set((s) => { const p = { ...s.projectThemes }; delete p[pid]; return { projectThemes: p }; }),
      setAccent:        (rgb)      => set({ accentOverride: rgb }),
      setCornerRadius:  (px)       => set({ cornerRadius: px }),
      setTransparency:  (v)        => set({ transparency: v }),
      setShadow:        (v)        => set({ shadowIntensity: v }),
      setGlow:          (v)        => set({ glowIntensity: v }),
      setAnimation:     (v)        => set({ animationLevel: v }),

      getActiveVars: () => {
        const { activeTheme, customVars, accentOverride } = get();
        const base = activeTheme === 'custom'
          ? R2_THEMES['engineering-dark'].vars
          : R2_THEMES[activeTheme as Exclude<R2ThemeId, 'custom'>].vars;
        return {
          ...base,
          ...customVars,
          ...(accentOverride ? { accent: accentOverride } : {}),
        };
      },

      autoDetectTheme: () => {
        const h = new Date().getHours();
        const { autoSwitch } = get();
        if (!autoSwitch.enabled) return get().activeTheme;
        return h >= 6 && h < 18 ? autoSwitch.dayTheme : autoSwitch.nightTheme;
      },
    }),
    { name: 'r2-display-experience-engine-v1' },
  ),
);

// ─── CSS Variable Injection ────────────────────────────────────────────────────
// Called from App.tsx whenever the theme changes.
export function applyThemeVars(themeId: R2ThemeId, overrides?: Partial<R2ThemeVars>): void {
  const root = document.documentElement;
  const base = themeId === 'custom'
    ? R2_THEMES['engineering-dark'].vars
    : R2_THEMES[themeId as Exclude<R2ThemeId, 'custom'>].vars;
  const vars: R2ThemeVars = { ...base, ...overrides };

  // Core palette
  root.style.setProperty('--bg',       vars.bgDeep);
  root.style.setProperty('--panel',    vars.bgPanel);
  root.style.setProperty('--panel-2',  vars.bgPanel2);
  root.style.setProperty('--border',   vars.border);
  root.style.setProperty('--muted',    vars.textSecondary);
  root.style.setProperty('--fg',       vars.textPrimary);
  root.style.setProperty('--accent',   vars.accent);
  root.style.setProperty('--accent-2', vars.accent2);

  // DEE-specific vars
  root.style.setProperty('--r2-bg-deep',      `rgb(${vars.bgDeep})`);
  root.style.setProperty('--r2-bg-panel',     `rgb(${vars.bgPanel})`);
  root.style.setProperty('--r2-bg-panel-2',   `rgb(${vars.bgPanel2})`);
  root.style.setProperty('--r2-border',       `rgb(${vars.border})`);
  root.style.setProperty('--r2-border-sub',   `rgb(${vars.borderSub})`);
  root.style.setProperty('--r2-text',         `rgb(${vars.textPrimary})`);
  root.style.setProperty('--r2-text-2',       `rgb(${vars.textSecondary})`);
  root.style.setProperty('--r2-text-3',       `rgb(${vars.textMuted})`);
  root.style.setProperty('--r2-accent',       `rgb(${vars.accent})`);
  root.style.setProperty('--r2-accent-2',     `rgb(${vars.accent2})`);
  root.style.setProperty('--r2-glow',         vars.glowColor);
  root.style.setProperty('--r2-shadow',       vars.shadow);
  root.style.setProperty('--r2-glass',        vars.glass);
  root.style.setProperty('--r2-glass-border', vars.glassBorder);
  root.style.setProperty('--r2-radius',       vars.radius);

  // Data attribute for CSS class-based overrides
  root.setAttribute('data-r2-theme', themeId);
  root.setAttribute('data-r2-dark', String(
    themeId !== 'apple-white' && themeId !== 'founder-gold',
  ));
}
